import React, { useEffect, useState } from 'react';
import ScrollableSheetViewer from './components/ScrollableSheetViewer';
import MetadataPanel from './components/MetadataPanel';
import QuickRefForm from './components/QuickAdd/QuickRefForm';
import FrameworkRefForm from './components/QuickAdd/FrameworkRefForm';
import ComboTermForm from './components/QuickAdd/ComboTermForm';
import NotCallOffForm from './components/QuickAdd/NotCallOffForm';

type NoticeMetadata = {
  id: number;
  title: string;
  description: string;
  earliest_start_date: string | null;
  latest_end_date: string | null;
};

type SheetData = string[][];

const FrameworkManager: React.FC = () => {
  const [sheet1, setSheet1] = useState<SheetData>([]);
  const [sheet2, setSheet2] = useState<SheetData>([]);
  const [sheet3, setSheet3] = useState<SheetData>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<NoticeMetadata | null>(null);
  const [activeQuickAdd, setActiveQuickAdd] = useState<'quickRef' | 'framework' | 'combo' | 'notCallOff' | null>(null);

  useEffect(() => {
    const fetchSheet = (sheetName: string, setter: React.Dispatch<React.SetStateAction<SheetData>>) => {
      fetch(`/api/frameworks/${sheetName}`, {
        headers: { 'Content-Type': 'application/json' },
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to fetch ${sheetName}`);
          return res.json();
        })
        .then((json) => {
          if (!json?.data || !Array.isArray(json.data)) throw new Error(`Invalid data in ${sheetName}`);
          setter(json.data);
        })
        .catch((err) => {
          console.error(`Error loading ${sheetName}:`, err);
          setter([]);
        });
    };

    fetchSheet('sheet1', setSheet1);
    fetchSheet('sheet2', setSheet2);
    fetchSheet('sheet3', setSheet3);
  }, []);

  const handleRowClick = async (guid: string) => {
    const encodedGuid = encodeURIComponent(guid);
    try {
      const response = await fetch(`/api/notice-metadata/${encodedGuid}`);
      const result = await response.json();
      if (result.status === 'success') {
        setSelectedMetadata(result.data);
      } else {
        setSelectedMetadata(null);
      }
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      setSelectedMetadata(null);
    }
  };

  const closeModal = () => setSelectedMetadata(null);

  const handleCellEdit = async (sheetName: string, rowIndex: number, newRow: string[]) => {
    try {
      const response = await fetch('/api/update-row', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheet_name: sheetName,
          row_index: rowIndex,
          values: newRow
        })
      });

      const result = await response.json();
      if (result.status !== 'success') {
        console.error(
          `Update failed for ${sheetName} row ${rowIndex}:`,
          result.detail || result.message || result
        );
        return;
      }

      // Update local state if backend update succeeded
      if (sheetName === 'Sheet1') {
        setSheet1(prev => {
          const updated = [...prev];
          updated[rowIndex] = newRow;
          return updated;
        });
      } else if (sheetName === 'Sheet2') {
        setSheet2(prev => {
          const updated = [...prev];
          updated[rowIndex] = newRow;
          return updated;
        });
      } else if (sheetName === 'Sheet3') {
        setSheet3(prev => {
          const updated = [...prev];
          updated[rowIndex] = newRow;
          return updated;
        });
      }
    } catch (err) {
      console.error(`Failed to update ${sheetName} row ${rowIndex}:`, err);
    }
  };

    return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
        {/* Single row: Buttons + Forms + Metadata panel */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '20px' }}>
          {/* Left: Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              style={{
                backgroundColor: activeQuickAdd === 'quickRef' ? '#2d8f3c' : '',
                color: 'white',
                padding: '10px',
                borderRadius: '5px'
              }}
              onClick={() => setActiveQuickAdd('quickRef')}
            >
              Quick Ref Term
            </button>
            <button
              style={{
                backgroundColor: activeQuickAdd === 'framework' ? '#2d8f3c' : '',
                color: 'white',
                padding: '10px',
                borderRadius: '5px'
              }}
              onClick={() => setActiveQuickAdd('framework')}
            >
              Framework Identifier
            </button>
            <button
              style={{
                backgroundColor: activeQuickAdd === 'combo' ? '#2d8f3c' : '',
                color: 'white',
                padding: '10px',
                borderRadius: '5px'
              }}
              onClick={() => setActiveQuickAdd('combo')}
            >
              Combo Term
            </button>
            <button
              style={{
                backgroundColor: activeQuickAdd === 'notCallOff' ? '#2d8f3c' : '',
                color: 'white',
                padding: '10px',
                borderRadius: '5px'
              }}
              onClick={() => setActiveQuickAdd('notCallOff')}
            >
              Not a Call-off
            </button>
          </div>

          {/* Middle: Forms */}
          <div style={{ width: '28%', border: '1px dashed #ccc', padding: '12px', borderRadius: '8px' }}>
            {activeQuickAdd === 'quickRef' && (
              <QuickRefForm onAdd={async (guid: string, term: string) => {
                const newRow = [guid, term];
                setSheet1(prev => [...prev, newRow]);
                try {
                  const response = await fetch('/api/append-row', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sheet_name: 'Sheet1', row: newRow })
                  });
                  const result = await response.json();
                  if (result.status !== 'success') {
                    console.error('Failed to append row to Sheet1:', result.detail || result.message || result);
                  }
                } catch (err) {
                  console.error('Error appending row to Sheet1:', err);
                }
              }} />
            )}
            {activeQuickAdd === 'framework' && (
              <FrameworkRefForm onAddToBothSheets={async (guid: string, terms: string[]) => {
                const [term1, ...restTerms] = terms;
                const sheet1Row = [guid, term1, restTerms.join(', ')];
                const sheet2Row = [guid, ...terms];
                setSheet1(prev => [...prev, sheet1Row]);
                setSheet2(prev => [...prev, sheet2Row]);
                const postRow = async (sheetName: string, row: string[]) => {
                  try {
                    const response = await fetch('/api/append-row', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ sheet_name: sheetName, row })
                    });
                    const result = await response.json();
                    if (result.status !== 'success') {
                      console.error(`Failed to append row to ${sheetName}:`, result.detail || result.message || result);
                    }
                  } catch (err) {
                    console.error(`Error appending row to ${sheetName}:`, err);
                  }
                };
                await Promise.all([
                  postRow('Sheet1', sheet1Row),
                  postRow('Sheet2', sheet2Row)
                ]);
              }} />
            )}
            {activeQuickAdd === 'combo' && (
              <ComboTermForm onAddToSheet3={async (row: string[]) => {
                setSheet3(prev => [...prev, row]);
                try {
                  const response = await fetch('/api/append-row', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sheet_name: 'Sheet3', row })
                  });
                  const result = await response.json();
                  if (result.status !== 'success') {
                    console.error('Failed to append row to Sheet3:', result.detail || result.message || result);
                  }
                } catch (err) {
                  console.error('Error appending row to Sheet3:', err);
                }
              }} />
            )}
            {activeQuickAdd === 'notCallOff' && (
              <NotCallOffForm onAddToSheet3={async (row: string[]) => {
                setSheet3(prev => [...prev, row]);
                try {
                  const response = await fetch('/api/append-row', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sheet_name: 'Sheet3', row })
                  });
                  const result = await response.json();
                  if (result.status !== 'success') {
                    console.error('Failed to append row to Sheet3:', result.detail || result.message || result);
                  }
                } catch (err) {
                  console.error('Error appending row to Sheet3:', err);
                }
              }} />
            )}
          </div>

          {/* Right: Metadata Panel */}
          <div style={{ width: '40%', overflowY: 'auto' }}>
            {selectedMetadata && (
              <MetadataPanel
                metadata={{
                  title: selectedMetadata.title,
                  description: selectedMetadata.description,
                  startDate: selectedMetadata.earliest_start_date || undefined,
                  endDate: selectedMetadata.latest_end_date || undefined,
                  millLink: `https://mill.tussell.com/notices/${selectedMetadata.id}/edit`
                }}
                onClose={closeModal}
              />
            )}
          </div>
        </div>

        {/* Bottom row: Spreadsheet viewers */}
        <div style={{ display: 'flex', gap: '20px', overflow: 'auto', height: 'calc(100vh - 200px)' }}>
        <ScrollableSheetViewer
          data={sheet1 || []}
          title="Sheet 1: Quick refs"
          onRowClick={handleRowClick}
          onEdit={(rowIndex, newRow) => handleCellEdit('Sheet1', rowIndex, newRow)}
          allowBlankEdit={true}
        />
        <ScrollableSheetViewer
          data={sheet2 || []}
          title="Sheet 2: Framework identifiers"
          onRowClick={handleRowClick}
          onEdit={(rowIndex, newRow) => handleCellEdit('Sheet2', rowIndex, newRow)}
          allowBlankEdit={true}
        />
        <ScrollableSheetViewer
          data={sheet3 || []}
          title="Sheet 3: Combos & Not-a-call-off"
          onRowClick={handleRowClick}
          onEdit={(rowIndex, newRow) => handleCellEdit('Sheet3', rowIndex, newRow)}
          guidColumnIndex={1}
          allowBlankEdit={true}
        />
        </div>
    </div>
    );
};

export default FrameworkManager;